import { History, LoaderCircle, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiClient,
  type RuleReview,
  type RuleReviewDetail,
  type RuleReviewProposal
} from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { Select } from "@/shared/components/Select";
import { usePolling } from "@/shared/hooks/usePolling";
import type { Mention } from "@/shared/types/mention";
import { formatDateTime, formatInteger } from "@/shared/utils/format";
import {
  RULE_REVIEW_COMMENT_MAX_LENGTH,
  RULE_REVIEW_MAX_MENTIONS,
  RULE_REVIEW_OUTCOME_LABELS,
  RULE_REVIEW_PROPOSAL_KIND_LABELS,
  RULE_REVIEW_TASKS,
  RULE_REVIEW_TASK_LABELS,
  isRuleReviewActive,
  type RuleReviewOutcome,
  type RuleReviewTask
} from "@/shared/vocabulary/rule-reviews";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { applyRuleReviewProposal, isAfterEditable } from "./rule-review-apply";

/** De quanto em quanto tempo a consultoria em andamento é perguntada. A
 *  chamada à IA leva 30–90 s: perguntar mais rápido só gasta cerca. */
export const RULE_REVIEW_POLL_MS = 5000;
/** Quantos títulos da seleção aparecem antes do "e mais N". */
const SELECTION_PREVIEW = 5;
const NO_MENTIONS: number[] = [];

/** O que a página manda pro painel. `nonce` muda a cada ordem, pra a
 *  mesma ordem repetida (clicar de novo no botão) valer outra vez. */
export type RuleReviewCommand =
  | { nonce: number; action: "compose"; mentionIds: number[] }
  | { nonce: number; action: "open"; reviewId: number };

type View = { kind: "compose" } | { kind: "review"; id: number };

type ItemState = {
  busy?: boolean;
  error?: string | null;
  note?: string | null;
  /** Aplicada nesta sessão sem o registro ter voltado do servidor (o
   *  cadastro aceitou, o registro falhou): trava o reenvio. */
  done?: RuleReviewOutcome;
};

export type RuleReviewPanelProps = {
  token: string;
  /** Aberto ou escondido. Escondido continua MONTADO: a consultoria em
   *  andamento segue sendo acompanhada com o painel fechado, e reabrir
   *  mostra o ponto em que ela está. */
  open: boolean;
  command: RuleReviewCommand | null;
  /** A menção da página, quando a página a conhece (chip clicável). */
  lookupMention: (id: number) => Mention | undefined;
  openMention: (mention: Mention) => void;
  onClose: () => void;
  /** Visualizador só LÊ (2026-10-24): sem o formulário de pedir e sem
   *  "Aplicar"/"Descartar", que o servidor recusa. Padrão: pode. */
  canReview?: boolean;
};

/**
 * O painel "Pedir melhorias à IA" da página de Justificativas
 * (2026-10-21): a pessoa escreve o que acha, a IA devolve um parecer e
 * propostas de mudança nas regras de UMA tarefa, e cada proposta tem
 * "Aplicar" (pelas rotas de cadastro de sempre) e "Descartar".
 *
 * Acoplado à direita da lista; abaixo de ~1100 px vira folha por cima.
 * Abrir o painel não chama nada além da lista de consultorias anteriores
 * (uma vez, na primeira montagem). O pedido responde na hora e o painel
 * acompanha por polling (5 s, com a pausa do `Retry-After`) até a
 * consultoria fechar.
 */
export function RuleReviewPanel({
  token,
  open,
  command,
  lookupMention,
  openMention,
  onClose,
  canReview = true
}: RuleReviewPanelProps) {
  const [view, setView] = useState<View>({ kind: "compose" });
  const [frozen, setFrozen] = useState<number[]>(NO_MENTIONS);
  const [task, setTask] = useState<RuleReviewTask>(RULE_REVIEW_TASKS[0]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [review, setReview] = useState<RuleReviewDetail | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);

  const [edits, setEdits] = useState<Record<number, string>>({});
  const [items, setItems] = useState<Record<number, ItemState>>({});
  const [droppedOpen, setDroppedOpen] = useState(false);

  const [history, setHistory] = useState<RuleReview[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Leituras de dentro de efeitos e de callbacks assíncronos.
  const reviewRef = useRef<RuleReviewDetail | null>(null);
  reviewRef.current = review;
  const loadSeq = useRef(0);

  // --- as consultorias anteriores: na montagem e quando uma fecha -----
  const loadHistory = useCallback(async () => {
    try {
      const page = await apiClient.listRuleReviews(token, { page: 1, size: 20 });
      setHistory(page.items);
      setHistoryError(null);
    } catch (failure) {
      setHistoryError(errorMessage(failure, "Não foi possível carregar as consultorias anteriores."));
    }
  }, [token]);
  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // --- abrir uma consultoria já pedida --------------------------------
  const showReview = useCallback(
    async (id: number) => {
      const seq = ++loadSeq.current;
      setView({ kind: "review", id });
      setReview(current => (current?.id === id ? current : null));
      setReviewError(null);
      setPausedUntil(null);
      setEdits({});
      setItems({});
      setDroppedOpen(false);
      try {
        const detail = await apiClient.getRuleReview(token, id);
        if (seq !== loadSeq.current) return;
        setReview(detail);
      } catch (failure) {
        if (seq !== loadSeq.current) return;
        setReviewError(errorMessage(failure, "Não foi possível abrir a consultoria."));
      }
    },
    [token]
  );

  // --- as ordens da página --------------------------------------------
  const seenNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!command || seenNonce.current === command.nonce) return;
    seenNonce.current = command.nonce;
    if (command.action === "open") {
      void showReview(command.reviewId);
      return;
    }
    // Consultoria andando não é trocada por um formulário novo: a
    // seleção nova vale quando ela terminar (só uma roda por empresa).
    const running = reviewRef.current;
    if (running && isRuleReviewActive(running.status)) return;
    loadSeq.current += 1;
    setFrozen(command.mentionIds);
    setView({ kind: "compose" });
    setReview(null);
    setReviewError(null);
    setFormError(null);
  }, [command, showReview]);

  // --- acompanhar enquanto ativa --------------------------------------
  const refresh = useCallback(async () => {
    const current = reviewRef.current;
    if (!current) return;
    const seq = loadSeq.current;
    try {
      const detail = await apiClient.getRuleReview(token, current.id);
      if (seq !== loadSeq.current) return;
      setReview(detail);
      setReviewError(null);
      setPausedUntil(null);
    } catch (failure) {
      if (seq !== loadSeq.current) return;
      // Um 429 é aviso pra esperar: a tela pausa o polling pelo tempo
      // que o servidor pediu e segue mostrando o que já tem.
      if (failure instanceof ApiError && failure.retryAfterSeconds !== null) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setReviewError(errorMessage(failure, "Não foi possível atualizar a consultoria."));
    }
  }, [token]);

  const active = view.kind === "review" && review !== null && isRuleReviewActive(review.status);
  usePolling({
    enabled: active,
    onTick: () => void refresh(),
    pausedUntil,
    intervalMs: RULE_REVIEW_POLL_MS
  });

  // Quando a consultoria fecha, a lista de anteriores ganha a nova.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && !active) void loadHistory();
    wasActive.current = active;
  }, [active, loadHistory]);

  // --- pedir -----------------------------------------------------------
  const trimmed = comment.trim();
  const selectionError =
    frozen.length === 0
      ? "Marque ao menos uma menção na lista para pedir a consultoria."
      : frozen.length > RULE_REVIEW_MAX_MENTIONS
        ? `A consultoria aceita até ${RULE_REVIEW_MAX_MENTIONS} menções por vez.`
        : null;
  const canSubmit =
    !submitting &&
    selectionError === null &&
    trimmed.length > 0 &&
    trimmed.length <= RULE_REVIEW_COMMENT_MAX_LENGTH;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const created = await apiClient.requestRuleReview(token, {
        mention_ids: frozen,
        task,
        comment: trimmed
      });
      loadSeq.current += 1;
      setReview({ ...created, mentions: [] });
      setView({ kind: "review", id: created.id });
      setReviewError(null);
      setPausedUntil(null);
      setEdits({});
      setItems({});
      setDroppedOpen(false);
    } catch (failure) {
      setFormError(errorMessage(failure, "Não foi possível pedir a consultoria. Tente de novo."));
    } finally {
      setSubmitting(false);
    }
  };

  const askAgain = () => {
    if (!review) return;
    loadSeq.current += 1;
    setFrozen(review.mention_ids);
    setTask(review.task);
    setComment(review.comment);
    setFormError(null);
    setReview(null);
    setView({ kind: "compose" });
  };

  // --- aplicar / descartar --------------------------------------------
  const patchItem = (index: number, change: ItemState) =>
    setItems(current => ({ ...current, [index]: { ...current[index], ...change } }));

  /** Troca só o registro de desfechos, mantendo os rótulos das menções. */
  const adoptApplied = (updated: RuleReview) =>
    setReview(current =>
      current && current.id === updated.id ? { ...current, applied: updated.applied } : current
    );

  const decide = async (index: number, proposal: RuleReviewProposal, outcome: RuleReviewOutcome) => {
    const target = reviewRef.current;
    if (!target) return;
    patchItem(index, { busy: true, error: null, note: null });
    if (outcome === "applied") {
      // A proposta se aplica no cadastro do DONO da consultoria: a sombra
      // do concorrente quando há `competitor_id` (o `onCompetitor` cobre
      // exatamente premissas, subtemas e mensagens-chave), a empresa
      // quando não (`onCompetitor(null)` é o próprio cliente). O registro
      // do desfecho, logo abaixo, segue pela empresa.
      const result = await applyRuleReviewProposal(
        target.competitor_id === null ? apiClient : apiClient.onCompetitor(target.competitor_id),
        token,
        target.task,
        proposal,
        edits[index] ?? proposal.after,
        target.id
      );
      if (!result.ok) {
        patchItem(index, { busy: false, error: result.message });
        return;
      }
      if (result.note) patchItem(index, { note: result.note });
    }
    try {
      const updated = await apiClient.recordRuleReviewOutcome(token, target.id, index, outcome);
      adoptApplied(updated);
      patchItem(index, { busy: false });
    } catch (failure) {
      const reason = errorMessage(failure, "Tente de novo.");
      patchItem(
        index,
        outcome === "applied"
          ? {
              busy: false,
              done: "applied",
              error: `Aplicada no cadastro, mas o registro falhou: ${reason}`
            }
          : { busy: false, error: `Não foi possível registrar o descarte: ${reason}` }
      );
    }
  };

  // O rótulo da pílula: "CANAL · título" (a fonte na frente, porque o título
  // sozinho não dizia de onde era a menção), com o título inteiro no `title`.
  const labelOf = (id: number): string => {
    const detail = review?.mentions.find(mention => mention.id === id);
    const title = detail?.title || lookupMention(id)?.title || `Menção #${id}`;
    const where = detail?.channel || lookupMention(id)?.source;
    return where ? `${where} · ${title}` : title;
  };

  return (
    <>
      <div className="rule-review-backdrop" hidden={!open} onClick={onClose} aria-hidden="true" />
      <aside className="rule-review-panel" hidden={!open} aria-label="Pedir melhorias à IA">
        <header className="rule-review-head">
          <h3 className="rule-review-title">
            <Sparkles size={16} aria-hidden="true" />
            Pedir melhorias à IA
          </h3>
          <button
            type="button"
            className="rule-review-close"
            onClick={onClose}
            aria-label="Fechar o painel"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="rule-review-body">
          {view.kind === "compose" && !canReview && (
            <p className="rule-review-hint">
              Seu cargo só lê as consultorias. Abra uma das anteriores abaixo.
            </p>
          )}
          {view.kind === "compose" && canReview && (
            <form
              className="rule-review-form"
              onSubmit={event => {
                event.preventDefault();
                void submit();
              }}
            >
              <p className="rule-review-hint">
                A IA lê as menções e as regras de UMA tarefa e propõe mudanças. Ela não reclassifica
                nada, e nada é gravado até você aplicar.
              </p>

              <SelectionSummary ids={frozen} labelOf={labelOf} />
              {selectionError && (
                <p className="rule-review-warning" role="status">
                  {selectionError}
                </p>
              )}

              <label className="rule-review-field">
                <span>Tarefa</span>
                <Select value={task} onChange={event => setTask(event.target.value as RuleReviewTask)}>
                  {RULE_REVIEW_TASKS.map(option => (
                    <option key={option} value={option}>
                      {RULE_REVIEW_TASK_LABELS[option]}
                    </option>
                  ))}
                </Select>
              </label>

              <label className="rule-review-field">
                <span>O que você acha</span>
                <textarea
                  value={comment}
                  maxLength={RULE_REVIEW_COMMENT_MAX_LENGTH}
                  rows={6}
                  placeholder="Ex.: está abrangente demais; o foco é X, Y e Z."
                  onChange={event => setComment(event.target.value)}
                />
                <small className="rule-review-counter">
                  {formatInteger(comment.length)} / {formatInteger(RULE_REVIEW_COMMENT_MAX_LENGTH)}
                </small>
              </label>

              {formError && (
                <p className="form-error" role="alert">
                  {formError}
                </p>
              )}
              <button type="submit" className="primary-button" disabled={!canSubmit}>
                {submitting && <LoaderCircle size={15} className="spin" aria-hidden="true" />}
                Pedir à IA
              </button>
            </form>
          )}

          {view.kind === "review" && !review && !reviewError && (
            <p className="rule-review-wait" role="status">
              <LoaderCircle size={15} className="spin" aria-hidden="true" />
              Carregando a consultoria…
            </p>
          )}
          {view.kind === "review" && !review && reviewError && (
            <>
              <p className="form-error" role="alert">
                {reviewError}
              </p>
              <button type="button" className="secondary-button" onClick={() => void showReview(view.id)}>
                Tentar de novo
              </button>
            </>
          )}

          {view.kind === "review" && review && (
            <div className="rule-review-result">
              <p className="rule-review-meta">
                <strong>{RULE_REVIEW_TASK_LABELS[review.task]}</strong>
                {" · "}
                {formatInteger(review.mention_ids.length)}{" "}
                {review.mention_ids.length === 1 ? "menção" : "menções"}
                {review.competitor_name && (
                  <>
                    {" · "}
                    <span className="report-criteria-chip">
                      Sobre o concorrente {review.competitor_name}
                    </span>
                  </>
                )}
              </p>
              <blockquote className="rule-review-comment">{review.comment}</blockquote>

              {reviewError && (
                <p className="rule-review-warning" role="status">
                  {reviewError}
                </p>
              )}

              {isRuleReviewActive(review.status) && (
                <p className="rule-review-wait" role="status">
                  <LoaderCircle size={15} className="spin" aria-hidden="true" />
                  {review.status === "QUEUED"
                    ? "Na fila — a IA começa em instantes."
                    : "A IA está analisando. Leva de 30 a 90 segundos; você pode fechar o painel, o sino avisa quando terminar."}
                </p>
              )}

              {review.status === "ERRORED" && (
                <>
                  <p className="form-error" role="alert">
                    {review.message || "A consultoria não terminou."}
                  </p>
                  <button type="button" className="secondary-button" onClick={askAgain}>
                    Pedir de novo
                  </button>
                </>
              )}

              {review.status === "FINISHED" && (
                <>
                  <h4 className="rule-review-section">Parecer</h4>
                  <p className="rule-review-summary">{review.summary || "A IA não escreveu parecer."}</p>

                  {review.dropped.length > 0 && (
                    <div className="rule-review-dropped">
                      <button
                        type="button"
                        className="text-button"
                        aria-expanded={droppedOpen}
                        onClick={() => setDroppedOpen(value => !value)}
                      >
                        A IA sugeriu {formatInteger(review.dropped.length)}{" "}
                        {review.dropped.length === 1 ? "coisa que não cabe" : "coisas que não cabem"}
                      </button>
                      {droppedOpen && (
                        <ul>
                          {review.dropped.map((item, index) => (
                            <li key={index}>{item.reason}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <h4 className="rule-review-section">Propostas ({formatInteger(review.proposals.length)})</h4>
                  {review.proposals.length === 0 && (
                    <p className="rule-review-hint">
                      A IA não propôs mudança nas regras. O parecer acima diz por quê.
                    </p>
                  )}
                  <ol className="rule-review-proposals">
                    {review.proposals.map((proposal, index) => (
                      <ProposalCard
                        key={index}
                        index={index}
                        proposal={proposal}
                        review={review}
                        state={items[index]}
                        after={edits[index] ?? proposal.after}
                        onEdit={value => setEdits(current => ({ ...current, [index]: value }))}
                        labelOf={labelOf}
                        lookupMention={lookupMention}
                        openMention={openMention}
                        onDecide={outcome => void decide(index, proposal, outcome)}
                        canDecide={canReview}
                      />
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}

          <details className="rule-review-history">
            <summary>
              <History size={14} aria-hidden="true" />
              Consultorias anteriores
              {history ? ` (${formatInteger(history.length)})` : ""}
            </summary>
            {historyError && (
              <p className="form-error" role="alert">
                {historyError}
              </p>
            )}
            {history && history.length === 0 && <p className="rule-review-hint">Nenhuma consultoria ainda.</p>}
            {history && history.length > 0 && (
              <ul>
                {history.map(item => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="rule-review-history-item"
                      onClick={() => void showReview(item.id)}
                    >
                      <span>
                        <strong>{RULE_REVIEW_TASK_LABELS[item.task]}</strong> ·{" "}
                        {formatInteger(item.mention_ids.length)}{" "}
                        {item.mention_ids.length === 1 ? "menção" : "menções"}
                        {item.competitor_name ? ` · concorrente ${item.competitor_name}` : ""}
                      </span>
                      <small>
                        {statusLabel(item.status)} · {when(item.requested_at)}
                        {item.requested_by_name ? ` · ${item.requested_by_name}` : ""}
                      </small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
      </aside>
    </>
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case "QUEUED":
      return "Na fila";
    case "STARTED":
      return "Em andamento";
    case "FINISHED":
      return "Concluída";
    default:
      return "Com erro";
  }
}

function when(value: string | null): string {
  const parsed = parseServerTime(value);
  return parsed === null ? "" : formatDateTime(new Date(parsed));
}

function SelectionSummary({ ids, labelOf }: { ids: number[]; labelOf: (id: number) => string }) {
  return (
    <div className="rule-review-selection">
      <p className="rule-review-meta">
        <strong>{formatInteger(ids.length)}</strong>{" "}
        {ids.length === 1 ? "menção marcada" : "menções marcadas"}{" "}
        <span className="rule-review-muted">(até {RULE_REVIEW_MAX_MENTIONS})</span>
      </p>
      {ids.length > 0 && (
        <ul>
          {ids.slice(0, SELECTION_PREVIEW).map(id => (
            <li key={id}>{labelOf(id)}</li>
          ))}
          {ids.length > SELECTION_PREVIEW && <li>e mais {formatInteger(ids.length - SELECTION_PREVIEW)}</li>}
        </ul>
      )}
    </div>
  );
}

function ProposalCard({
  index,
  proposal,
  review,
  state,
  after,
  onEdit,
  labelOf,
  lookupMention,
  openMention,
  onDecide,
  canDecide
}: {
  index: number;
  proposal: RuleReviewProposal;
  review: RuleReviewDetail;
  state: ItemState | undefined;
  after: string;
  onEdit: (value: string) => void;
  labelOf: (id: number) => string;
  lookupMention: (id: number) => Mention | undefined;
  openMention: (mention: Mention) => void;
  onDecide: (outcome: RuleReviewOutcome) => void;
  canDecide: boolean;
}) {
  const recorded = [...review.applied].reverse().find(entry => entry.index === index);
  const decided = recorded?.outcome ?? state?.done ?? null;
  const busy = state?.busy === true;
  const editable = isAfterEditable(proposal);
  const term =
    proposal.kind === "theme_add_term" || proposal.kind === "theme_remove_term" ? proposal.term : null;

  return (
    <li className="rule-review-proposal" data-outcome={decided ?? undefined}>
      <header>
        <span className="rule-review-kind">{RULE_REVIEW_PROPOSAL_KIND_LABELS[proposal.kind]}</span>
        <strong className="rule-review-target">{proposal.target_label}</strong>
      </header>

      {term !== null && (
        <p className="rule-review-term">
          Termo: <code>{term}</code>
        </p>
      )}

      <div className="rule-review-diff">
        {proposal.kind !== "rule_create" && (
          <div>
            <span className="rule-review-diff-label">Antes</span>
            <p className="rule-review-before">{proposal.before || "(vazio)"}</p>
          </div>
        )}
        <div>
          <span className="rule-review-diff-label">Depois</span>
          {proposal.kind === "rule_deactivate" ? (
            <p className="rule-review-after">A premissa será desativada.</p>
          ) : editable && decided === null && canDecide ? (
            <textarea
              className="rule-review-edit"
              aria-label={`Texto proposto — ${proposal.target_label}`}
              value={after}
              rows={4}
              disabled={busy}
              onChange={event => onEdit(event.target.value)}
            />
          ) : (
            <p className="rule-review-after">{after || "(vazio)"}</p>
          )}
        </div>
      </div>

      {proposal.why && <p className="rule-review-why">{proposal.why}</p>}

      {proposal.mention_ids.length > 0 && (
        <div className="rule-review-mentions-block">
          <p className="rule-review-mentions-caption">
            {proposal.mention_ids.length === 1
              ? "Menção que motivou"
              : `Menções que motivaram (${proposal.mention_ids.length})`}
          </p>
          <ul className="rule-review-mentions" aria-label="Menções que motivaram a proposta">
            {proposal.mention_ids.map(id => {
              const mention = lookupMention(id);
              const label = labelOf(id);
              return (
                <li key={id}>
                  {mention ? (
                    <button
                      type="button"
                      className="rule-review-mention"
                      title={`${label} — abrir a menção`}
                      onClick={() => openMention(mention)}
                    >
                      {label}
                    </button>
                  ) : (
                    <span className="rule-review-mention is-static" title={label}>
                      {label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {state?.note && <p className="rule-review-note">{state.note}</p>}
      {state?.error && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}

      {decided !== null ? (
        <p className="rule-review-decided">
          {RULE_REVIEW_OUTCOME_LABELS[decided]}
          {recorded ? ` por ${recorded.applied_by_name || recorded.applied_by} em ${when(recorded.applied_at)}` : ""}
        </p>
      ) : canDecide ? (
        <div className="rule-review-actions">
          <button type="button" className="primary-button" disabled={busy} onClick={() => onDecide("applied")}>
            {busy && <LoaderCircle size={15} className="spin" aria-hidden="true" />}
            Aplicar
          </button>
          <button type="button" className="secondary-button" disabled={busy} onClick={() => onDecide("discarded")}>
            Descartar
          </button>
        </div>
      ) : null}
    </li>
  );
}
